// React
import { memo, useCallback } from "react";

// Helpers
import { transformDateToLocalDateString } from "helpers";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { TextField } from "ui/components/TextField/TextField";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";

// Tipovi
import { PricingBasicsStepProps } from "./types";

/** Datum od kojeg cjenik vrijedi i razlog promjene — kontekst cijele verzije. */
const PricingBasicsStep: React.FC<PricingBasicsStepProps> = (props: PricingBasicsStepProps) => {
  const { formValues, setFormValues, errors, version, isEffective, minValidFrom } = props;

  const handleValidFromChange = useCallback(
    (_name: string, date: Date | null) => {
      setFormValues((previous) => ({
        ...previous,
        validFrom: date ? date.toISOString().slice(0, 10) : null,
      }));
    },
    [setFormValues]
  );

  const handleNoteChange = useCallback(
    (e: any) => {
      const value = e?.target?.value ?? null;

      setFormValues((previous) => ({ ...previous, note: value }));
    },
    [setFormValues]
  );

  return (
    <>
      <PricingStepIntro
        title={
          version.isDraft
            ? "Uređivanje nacrta"
            : isEffective
              ? "Izmjena cjenika"
              : "Novi cjenik"
        }
        description={
          version.isDraft ? (
            <>
              Nacrt još ne vrijedi ni za koga, pa se mijenja u mjestu. Ispravite datum ili
              naziv i idite dalje kroz korake.
            </>
          ) : isEffective ? (
            <>
              Ovo je cjenik koji <strong>vrijedi danas</strong>, pa se ispravlja u mjestu.
              Ostavite li datum, promjena vrijedi unatrag, od{" "}
              <strong>{transformDateToLocalDateString(new Date(version.validFrom))}</strong>.
              Pomaknite ga ako želite da nove cijene počnu tek od nekog kasnijeg dana, čime
              nastaje nova verzija.
            </>
          ) : (
            <>
              Sve vrijednosti su preuzete iz cjenika koji vrijedi od{" "}
              <strong>{transformDateToLocalDateString(new Date(version.validFrom))}</strong>.
              Ta verzija je svoje odradila i ne prepisuje se. Pomaknite datum na dan{" "}
              <strong>od kojeg vrijedi novi cjenik</strong> i prođite korake.
            </>
          )
        }
      />

      <div className="flex flex-col gap-4 p-4 w-full max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row gap-4 md:items-start">
          <div className="w-full md:w-[240px]">
            {/* Raniji datum se ne nudi — cjenik u prošlosti backend odbija. */}
            <DatePicker
              name="validFrom"
              label="Vrijedi od"
              value={formValues.validFrom ? new Date(formValues.validFrom) : null}
              onChange={handleValidFromChange}
              error={errors.validFrom}
              minDate={minValidFrom}
              required
              width="full"
            />

            {/*
              `DatePicker` gasi svoju poruku čim je datum odabran, pa bi greška na već
              upisanom datumu prošla nijemo i „Nastavi" bi izgledao pokvareno.
            */}
            {formValues.validFrom && errors.validFrom && (
              <p className="text-sm text-red-600 mt-1">{errors.validFrom}</p>
            )}
          </div>

          <div className="flex-1">
            <TextField
              type="text"
              name="note"
              label="Naziv cjenika"
              placeholder="Vidi se u povijesti"
              value={formValues.note ?? ""}
              onChange={handleNoteChange}
              width="full"
            />
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-600">
            Dok datum još nije stigao, cjenik je <strong>nacrt</strong>. Ne vrijedi ni za jednog
            klijenta i možete ga mijenjati koliko god puta treba.
          </p>

          <p className="text-sm text-slate-600 mt-2">
            Kad taj dan dođe, po cjeniku se počinju izdavati računi i on se više ne mijenja. Nova
            promjena cijena tada znači <strong>novu verziju s kasnijim datumom</strong>. Stara
            ostaje zapisana takva kakva je bila, da se svaki izdani račun i dalje može objasniti.
          </p>

          <p className="text-sm text-slate-600 mt-2">
            Zato datum u prošlosti nije dopušten. Mijenjao bi cijene unatrag, ispod računa koji su
            već izdani.
          </p>
        </div>
      </div>
    </>
  );
};

export default memo(PricingBasicsStep);
